/**
 * Playlist Service
 */

import { getApiClient } from '../client';
import type {
  PlaylistListResponse,
  PlaylistDetail,
  PlaylistCreate,
  PlaylistCreated,
  PlaylistUpdate,
  AddTracksRequest,
  RemoveTracksRequest,
  PlaylistReorderRequest,
  PlaylistReorderResponse,
  PlaylistImportRequest,
  PlaylistImportResponse,
  WorkbenchPromptResponse,
  SnapshotResponse,
  PaginationParams,
} from '../types';

export const playlistService = {
  /**
   * List user's playlists with pagination
   */
  async list(params?: PaginationParams): Promise<PlaylistListResponse> {
    return getApiClient().get<PlaylistListResponse>('/playlists', params as Record<string, unknown>);
  },

  /**
   * Get detailed playlist info including all tracks
   * @param playlistId - Spotify playlist ID or URI
   */
  async get(playlistId: string): Promise<PlaylistDetail> {
    return getApiClient().get<PlaylistDetail>(`/playlists/${encodeURIComponent(playlistId)}`);
  },

  /**
   * Create a new playlist
   */
  async create(data: PlaylistCreate): Promise<PlaylistCreated> {
    return getApiClient().post<PlaylistCreated>('/playlists', data);
  },

  /**
   * Update playlist metadata
   * @param playlistId - Spotify playlist ID
   * @param data - Fields to update
   */
  async update(playlistId: string, data: PlaylistUpdate): Promise<{ message: string }> {
    return getApiClient().patch<{ message: string }>(
      `/playlists/${encodeURIComponent(playlistId)}`,
      data
    );
  },

  /**
   * Delete a playlist (user must be owner)
   * @param playlistId - Spotify playlist ID
   */
  async delete(playlistId: string): Promise<void> {
    return getApiClient().delete<void>(`/playlists/${encodeURIComponent(playlistId)}`);
  },

  /**
   * Add tracks to a playlist
   * @param playlistId - Spotify playlist ID
   * @param data - Track URIs and optional position
   */
  async addTracks(playlistId: string, data: AddTracksRequest): Promise<SnapshotResponse> {
    return getApiClient().post<SnapshotResponse>(
      `/playlists/${encodeURIComponent(playlistId)}/tracks`,
      data
    );
  },

  /**
   * Remove tracks from a playlist
   * @param playlistId - Spotify playlist ID
   * @param data - Track URIs to remove
   */
  async removeTracks(playlistId: string, data: RemoveTracksRequest): Promise<SnapshotResponse> {
    return getApiClient().delete<SnapshotResponse>(
      `/playlists/${encodeURIComponent(playlistId)}/tracks`,
      data
    );
  },

  /**
   * Reorder playlist using selected strategy
   * @param playlistId - Spotify playlist ID
   * @param data - Reorder configuration (strategy, applyToSpotify flag, startTrackId)
   */
  async reorder(playlistId: string, data: PlaylistReorderRequest): Promise<PlaylistReorderResponse> {
    // Convert camelCase to snake_case for API
    const apiData = {
      strategy: data.strategy ?? 'full_dj_mode',
      apply_to_spotify: data.applyToSpotify ?? false,
      start_track_id: data.startTrackId ?? null,
    };
    return getApiClient().post<PlaylistReorderResponse>(
      `/playlists/${encodeURIComponent(playlistId)}/reorder`,
      apiData
    );
  },

  /**
   * Import tracks from text to playlist
   * @param playlistId - Spotify playlist ID
   * @param data - Import configuration (text, mode, delimiter)
   */
  async import(playlistId: string, data: PlaylistImportRequest): Promise<PlaylistImportResponse> {
    return getApiClient().post<PlaylistImportResponse>(
      `/playlists/${encodeURIComponent(playlistId)}/import`,
      data
    );
  },

  /**
   * Generate LLM prompt for playlist curation
   * @param playlistId - Spotify playlist ID
   */
  async generatePrompt(playlistId: string): Promise<WorkbenchPromptResponse> {
    return getApiClient().post<WorkbenchPromptResponse>(
      `/workbench/playlists/${encodeURIComponent(playlistId)}/prompt`,
      {}
    );
  },
};
